// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import { PropTypes } from 'prop-types';
import Link from 'next/link';
import {
  buildUrl,
  getAsPathWithSlug,
  getMappedPageHref,
  triggerPostMessage,
  getAPIConfig,
  parseBoolean,
  routerPush,
  updateUrlWithIcid,
} from '../../../../../utils';
import withStyles from '../../../hoc/withStyles';

import styles from '../Anchor.style';

const getAsLinkPath = (IsSlugPathAdded, asPath, incomingUrl) => {
  return IsSlugPathAdded ? asPath || incomingUrl : getAsPathWithSlug(asPath || incomingUrl);
};

const checkIfPlayPauseButton = (element) => {
  const eleType = element.nodeName;
  const { className } = element;
  const hasVjsClassName =
    className.includes('video-play-btn') || className.includes('vjs-icon-placeholder');
  const parentType = element.parentNode.nodeName;
  return (eleType === 'IMG' && parentType === 'BUTTON') || hasVjsClassName;
};

/**
 * @param {object} props : Props for Anchor
 * @desc This is an anchor component. The prop anchorVariation determines the color of the anchor.
 * The values supported for anchorVariation are : primary, secondary, tertiary.
 * The prop fontSizeVariation determines the font size of the anchor.
 * The value supported for fontSizeVariation are : small, medium, large.
 * The values of these are picked up from the theme.
 * An additional prop noLink is added to support simple anchor or next's Link conditionally.
 */

const Anchor = ({
  children,
  to,
  asPath,
  className,
  scroll,
  noLink,
  handleLinkClick,
  shallow,
  title,
  target,
  url,
  text,
  dataLocator,
  IsSlugPathAdded,
  anchorVariation,
  fontSizeVariation,
  withCaret,
  overrideStyle,
  useRouterNavigation,
  updateRoute,
  useNextLinkRouter,
  isModule,
  moduleAnalyticsTag,
  ...other
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const updatedUrl = updateUrlWithIcid(url, moduleAnalyticsTag);
  const updatedToUrl = updateUrlWithIcid(to, moduleAnalyticsTag);
  const updatedCtaUrl = updateUrlWithIcid(asPath, moduleAnalyticsTag);
  const targetVal = target || '_self';
  let incomingUrl = updatedToUrl || updatedUrl;
  const asLinkPath =
    (updatedCtaUrl || incomingUrl) && getAsLinkPath(IsSlugPathAdded, updatedCtaUrl, incomingUrl);
  if (!noLink) {
    incomingUrl = getMappedPageHref(incomingUrl);
  }
  const handleAnchorClick = (e) => {
    if (useRouterNavigation || useNextLinkRouter) {
      e.preventDefault();
      setTimeout(() => {
        handleLinkClick(e);
        if (updateRoute) {
          let updatedAsPath = asPath;
          if (asPath && useNextLinkRouter) {
            if (asPath.includes('/us/')) {
              updatedAsPath = updatedAsPath.replace('/us/', '/');
            }
            if (asPath.includes('/ca/')) {
              updatedAsPath = updatedAsPath.replace('/ca/', '/');
            }
          }
          if (isModule && e.currentTarget !== e.target && checkIfPlayPauseButton(e.target)) {
            return; // necessary to return for video otherwise play pause button clicking navigates to anchor url
          }
          routerPush(to, updatedAsPath);
        }
      }, 0);
    } else {
      handleLinkClick(e);
    }
  };
  const hrefUrl = asLinkPath || buildUrl(incomingUrl);
  const apiConfigObj = getAPIConfig();
  let AnchorComponent = null;
  if (useRouterNavigation) {
    incomingUrl = asLinkPath;
  }
  if (children || text) {
    AnchorComponent =
      noLink || useRouterNavigation ? (
        <a
          href={buildUrl(incomingUrl)}
          className={className}
          onClick={handleAnchorClick}
          title={title || text}
          target={targetVal}
          data-locator={dataLocator}
          {...other}
        >
          {children}
        </a>
      ) : (
        <Link
          legacyBehavior
          href={incomingUrl}
          as={asLinkPath}
          shallow={shallow}
          scroll={parseBoolean(scroll)}
          passHref={useNextLinkRouter}
        >
          <a
            className={className}
            href={hrefUrl}
            title={title || text}
            target={targetVal}
            onClick={useNextLinkRouter ? handleAnchorClick : undefined}
            {...(apiConfigObj.isAppChannel
              ? {
                  onClick: () => triggerPostMessage(to || asLinkPath),
                }
              : '')}
            data-locator={dataLocator}
            {...other}
          >
            {children || text}
          </a>
        </Link>
      );
  }
  return AnchorComponent;
};

Anchor.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any,
  to: PropTypes.string,
  asPath: PropTypes.string,
  scroll: PropTypes.string,
  className: PropTypes.string.isRequired,
  noLink: PropTypes.bool,
  handleLinkClick: PropTypes.func,
  shallow: PropTypes.bool,
  title: PropTypes.string,
  target: PropTypes.string,
  url: PropTypes.string,
  text: PropTypes.string,
  dataLocator: PropTypes.string,
  IsSlugPathAdded: PropTypes.bool,
  overrideStyle: PropTypes.string,
  useRouterNavigation: PropTypes.bool,
  updateRoute: PropTypes.bool,
  useNextLinkRouter: PropTypes.bool,
};

Anchor.defaultProps = {
  asPath: '',
  scroll: '',
  noLink: false,
  handleLinkClick: () => {},
  shallow: false,
  title: '',
  target: '',
  url: '',
  text: '',
  dataLocator: '',
  IsSlugPathAdded: false,
  children: null,
  to: undefined,
  overrideStyle: '',
  useRouterNavigation: false,
  updateRoute: true,
  useNextLinkRouter: false,
};

export default withStyles(Anchor, styles);
export { Anchor as AnchorVanilla };
